import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Rocket, 
  FileText, 
  Globe, 
  Users, 
  Settings, 
  HelpCircle,
  Search,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Lightbulb,
  ArrowLeft
} from "lucide-react";
import CosmicBackground from "@/components/CosmicBackground";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GUIDE_SECTIONS = [
  {
    id: 'getting-started',
    icon: Rocket,
    title: 'Getting Started',
    description: 'New to ThePromptSpace? Start here.',
    topics: [
      {
        title: 'Creating an Account',
        content: `Getting started with ThePromptSpace is simple:

• **Email Sign-up**: Enter your email and create a password
• **Google Sign-in**: One-click authentication with your Google account
• **GitHub Sign-in**: Connect with your GitHub account for developer-friendly access

After signing up, you'll be prompted to choose a username and complete your profile.`
      },
      {
        title: 'Platform Overview',
        content: `ThePromptSpace is built around three core features:

• **Prompts**: Browse, create, and share AI prompts with the community
• **GEO Metadata**: Our unique system for categorizing and discovering prompts by intent, use case, and AI model
• **Groups**: Join communities of like-minded creators to collaborate and share knowledge

The platform is designed to help you build a portfolio of your best AI prompts and get recognized for your work.`
      },
      {
        title: 'Navigating the Interface',
        content: `Key areas of the platform:

• **Home**: Your starting point with featured prompts and platform highlights
• **Explore Prompts**: Browse all community prompts with filters
• **Groups**: Find and join communities
• **Profile**: Manage your prompts, saved items, and settings
• **Create Prompt**: Start crafting your AI prompts

Use the hamburger menu (☰) on any page to access all navigation options.`
      }
    ]
  },
  {
    id: 'creating-prompts',
    icon: FileText,
    title: 'Creating Prompts',
    description: 'Learn how to craft effective AI prompts.',
    topics: [
      {
        title: 'Writing Great Titles',
        content: `Your title is the first thing users see. Make it count:

• **Be Specific**: "Business Email Generator for Cold Outreach" > "Email Writer"
• **Include the Output**: Tell users what they'll get
• **Keep it Concise**: Aim for 5-10 words
• **Use Action Words**: Generate, Create, Analyze, Transform

A strong title improves discoverability and sets clear expectations.`
      },
      {
        title: 'Crafting Prompt Content',
        content: `The prompt content is the actual text users will copy and use:

• **Clear Instructions**: Start with what the AI should do
• **Context Setting**: Provide role or persona if needed
• **Input Placeholders**: Use [brackets] for user-specific inputs
• **Output Format**: Specify how results should be structured
• **Examples**: Include sample outputs when helpful

Remember: The best prompts are reusable and adaptable.`
      },
      {
        title: 'Adding Descriptions',
        content: `The description helps users understand your prompt's purpose:

• **Explain the Use Case**: When would someone use this?
• **Highlight Benefits**: What makes this prompt effective?
• **List Requirements**: Any specific inputs needed?
• **Mention Tested Models**: Which AI models work best?

A good description increases saves and engagement.`
      },
      {
        title: 'Editing & Managing Prompts',
        content: `You have full control over your prompts:

• **Edit**: Update title, description, content, or GEO metadata anytime
• **Delete**: Remove prompts you no longer want public
• **View Stats**: See likes, saves, and copies on your profile
• **Category Edits**: Limited to 3 category changes per prompt

Find all your prompts in your Profile under the "Prompts" tab.`
      }
    ]
  },
  {
    id: 'geo-metadata',
    icon: Globe,
    title: 'GEO Metadata',
    description: 'Our unique prompt categorization system.',
    topics: [
      {
        title: 'What is GEO?',
        content: `GEO (Generative Engine Optimization) is ThePromptSpace's proprietary system for categorizing AI prompts:

• **Discoverability**: Helps users find exactly what they need
• **Quality Signals**: Shows tested and verified prompts
• **Model Matching**: Recommends which AI models work best
• **Use Case Clarity**: Categorizes prompts by real-world application

GEO metadata makes your prompts more findable and trustworthy.`
      },
      {
        title: 'Intent Types',
        content: `Every prompt has a primary intent:

• **Generate**: Create new content (text, ideas, code)
• **Analyze**: Break down and understand information
• **Transform**: Convert content from one format to another
• **Chat**: Conversational and interactive use cases
• **Code**: Programming and development tasks

Choose the intent that best matches what your prompt produces.`
      },
      {
        title: 'Use Cases',
        content: `Use cases group prompts by application area:

**Business**: Marketing, Sales, Strategy, Customer Service
**Technical**: Development, Data Analysis, System Design
**Creative**: Writing, Design, Brainstorming, Storytelling
**Content**: Blog Posts, Social Media, Documentation
**Professional**: Legal, Medical, Academic, Research
**Productivity**: Task Management, Automation, Organization

Select the most specific use case that applies.`
      },
      {
        title: 'Recommended Models',
        content: `Indicate which AI models your prompt works best with:

• **ChatGPT-4**: OpenAI's flagship model
• **Claude 3**: Anthropic's advanced assistant
• **Gemini Pro**: Google's multimodal AI
• **Llama 3**: Meta's open-source model
• **Mistral**: Fast and efficient open model

Testing your prompt on multiple models helps users get consistent results.`
      },
      {
        title: 'Tested by Author',
        content: `The "Tested by Author" badge shows you've verified your prompt:

• Check this box when you've personally tested the prompt
• Users trust verified prompts more
• It improves your prompt's visibility in search
• Be honest—only mark as tested if you've actually tried it

This simple verification builds credibility with the community.`
      }
    ]
  },
  {
    id: 'groups-channels',
    icon: Users,
    title: 'Groups & Channels',
    description: 'Connect with the community.',
    topics: [
      {
        title: 'Joining Groups',
        content: `Groups are communities built around shared interests:

• **Browse Groups**: Find groups on the Groups page
• **Join Instantly**: Click "Join" to become a member
• **View Channels**: Access group discussions after joining
• **Read Rules**: Each group has guidelines—follow them

Groups are great for learning, sharing, and networking.`
      },
      {
        title: 'Creating Groups',
        content: `Start your own community:

• **Name & Description**: Make it clear what your group is about
• **Set Rules**: Define expectations for members
• **Create Channels**: Organize discussions by topic
• **Invite Members**: Share your group with others

You'll be the admin with full moderation controls.`
      },
      {
        title: 'Channel Communication',
        content: `Channels are where conversations happen:

• **Text Chat**: Real-time messaging with members
• **Mentions**: Use @username to notify specific people
• **Reactions**: React to messages with emojis
• **Announcements**: Admins can create announcement channels

Keep discussions on-topic and respectful.`
      },
      {
        title: 'Moderation',
        content: `Admins and moderators keep groups healthy:

• **Member Management**: Approve, mute, or ban members
• **Message Moderation**: Delete inappropriate content
• **Channel Settings**: Create, edit, or remove channels
• **Group Settings**: Update name, description, and rules

Good moderation builds a positive community.`
      }
    ]
  },
  {
    id: 'profile-settings',
    icon: Settings,
    title: 'Profile & Settings',
    description: 'Customize your experience.',
    topics: [
      {
        title: 'Customizing Your Profile',
        content: `Make your profile stand out:

• **Username**: Your unique identifier on the platform
• **Bio**: Short description (keep it under 160 characters)
• **Long Bio**: Extended about section for your profile page
• **Tagline**: One-liner that appears with your name
• **Focus Tags**: Keywords that describe your expertise

A complete profile builds trust and attracts followers.`
      },
      {
        title: 'Avatar Selection',
        content: `Choose from our avatar gallery:

• **Pre-made Avatars**: Select from curated designs
• **Profile Colors**: Customize your username color
• **Post Colors**: Set a signature color for your prompts

Your avatar appears on prompts, comments, and group messages.`
      },
      {
        title: 'Notification Preferences',
        content: `Control what notifications you receive:

• **Likes**: When someone likes your prompt
• **Comments**: When someone comments on your prompt
• **Saves**: When someone saves your prompt
• **Email Notifications**: Receive updates via email
• **Push Notifications**: Browser notifications (if enabled)

Find these settings in Settings → Notifications.`
      },
      {
        title: 'Privacy Controls',
        content: `Manage your privacy:

• **Private Profile**: Hide your profile from non-followers
• **Hide Likes**: Don't show what you've liked
• **Hide Posts**: Make your prompts private
• **Hide Comments**: Hide your comment activity

Balance visibility with privacy based on your preferences.`
      }
    ]
  },
  {
    id: 'faq',
    icon: HelpCircle,
    title: 'FAQ',
    description: 'Common questions answered.',
    topics: [
      {
        title: 'Is ThePromptSpace free?',
        content: `Yes! ThePromptSpace is free to use:

• **Browse & Save**: Unlimited prompt browsing and saving
• **Create Prompts**: Share unlimited prompts with the community
• **Join Groups**: Participate in any public group
• **Full Profile**: Complete access to profile features

We may introduce premium features in the future, but core functionality will always be free.`
      },
      {
        title: 'How do I get more visibility?',
        content: `Increase your prompt visibility:

• **Complete GEO Metadata**: Fully categorized prompts rank higher
• **Write Clear Titles**: Descriptive titles improve search results
• **Engage with Others**: Comment, like, and save other prompts
• **Join Groups**: Share your work in relevant communities
• **Consistency**: Regular posting builds your reputation

Quality always wins—focus on creating genuinely useful prompts.`
      },
      {
        title: 'Can I delete my account?',
        content: `Yes, you can delete your account:

• Go to Settings → Account
• Scroll to "Danger Zone"
• Click "Delete Account"
• Confirm your decision

Note: This permanently removes all your prompts, comments, and data. This action cannot be undone.`
      },
      {
        title: 'How do I report inappropriate content?',
        content: `Report content that violates our guidelines:

• **Prompts**: Click the three-dot menu → Report
• **Messages**: Long-press or right-click → Report
• **Users**: Visit their profile → Report

Our moderation team reviews all reports and takes appropriate action.`
      },
      {
        title: 'Need more help?',
        content: `Get support through these channels:

• **Discord**: Join our community for real-time help
• **Support Page**: Visit our Support/Donate page
• **Email**: Contact us directly

We're here to help you succeed on ThePromptSpace!`
      }
    ]
  }
];

const Docs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const filteredSections = GUIDE_SECTIONS.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      section.title.toLowerCase().includes(query) ||
      section.description.toLowerCase().includes(query) ||
      section.topics.some(
        topic =>
          topic.title.toLowerCase().includes(query) ||
          topic.content.toLowerCase().includes(query)
      )
    );
  });

  const totalTopics = GUIDE_SECTIONS.reduce((acc, section) => acc + section.topics.length, 0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": GUIDE_SECTIONS.find(s => s.id === 'faq')?.topics.map(topic => ({
      "@type": "Question",
      "name": topic.title,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": topic.content.replace(/\*\*/g, '').replace(/•/g, '-')
      }
    })) || []
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Documentation - ThePromptSpace | Guides & Tutorials</title>
        <meta name="description" content="Learn how to use ThePromptSpace. Comprehensive guides on creating prompts, GEO metadata, groups, profile settings, and more." />
        <link rel="canonical" href="https://thepromptspace.com/docs" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <CosmicBackground />

      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-black/20 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 py-16 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-4 bg-gradient-to-r from-[#00C2FF] via-[#7000FF] to-[#FF00A8] bg-clip-text text-transparent">
            Documentation
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Your complete guide to ThePromptSpace. Learn how to create, discover, and share AI prompts.
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 focus:border-[#00C2FF]/50"
              />
            </div>
          </div>

          {/* Stats */}
          <p className="text-sm text-muted-foreground">
            {GUIDE_SECTIONS.length} Guides • {totalTopics} Topics
          </p>
        </div>
      </section>

      {/* Guide Cards Grid */}
      <section className="relative z-10 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {filteredSections.map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSection === section.id;
              
              return (
                <button
                  key={section.id}
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                  className={`group p-6 rounded-2xl border text-left transition-all duration-300 ${
                    isExpanded 
                      ? 'bg-white/10 border-[#00C2FF]/50 shadow-[0_0_30px_rgba(0,194,255,0.15)]' 
                      : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg transition-colors ${
                        isExpanded ? 'bg-[#00C2FF]/20' : 'bg-white/10 group-hover:bg-[#00C2FF]/10'
                      }`}>
                        <Icon className={`w-5 h-5 ${isExpanded ? 'text-[#00C2FF]' : 'text-white/70'}`} />
                      </div>
                      <h3 className="font-semibold text-lg">{section.title}</h3>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-[#00C2FF]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-white/70" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                  <p className="text-xs text-muted-foreground mt-2">{section.topics.length} topics</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expanded Content */}
      {expandedSection && (
        <section className="relative z-10 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              {filteredSections
                .filter(s => s.id === expandedSection)
                .map(section => {
                  const Icon = section.icon;
                  return (
                    <div 
                      key={section.id}
                      className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-md"
                    >
                      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                        <div className="p-2 rounded-lg bg-[#00C2FF]/20">
                          <Icon className="w-6 h-6 text-[#00C2FF]" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold">{section.title}</h2>
                          <p className="text-sm text-muted-foreground">{section.description}</p>
                        </div>
                      </div>

                      <Accordion type="single" collapsible className="space-y-2">
                        {section.topics.map((topic, index) => (
                          <AccordionItem 
                            key={index} 
                            value={`topic-${index}`}
                            className="border border-white/10 rounded-lg px-4 bg-white/5"
                          >
                            <AccordionTrigger className="text-left hover:no-underline py-4">
                              <span className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-[#00C2FF]/50" />
                                {topic.title}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                              <div className="prose prose-invert prose-sm max-w-none">
                                {topic.content.split('\n\n').map((paragraph, pIndex) => (
                                  <p key={pIndex} className="text-muted-foreground mb-3 last:mb-0 whitespace-pre-line">
                                    {paragraph.split('**').map((part, partIndex) => 
                                      partIndex % 2 === 1 ? (
                                        <strong key={partIndex} className="text-foreground font-medium">{part}</strong>
                                      ) : part
                                    )}
                                  </p>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* Tip Box */}
      <section className="relative z-10 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-[#00C2FF]/10 to-[#7000FF]/10 border border-[#00C2FF]/20 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-[#00C2FF]/20">
                  <Lightbulb className="w-5 h-5 text-[#00C2FF]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Need more help?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Join our Discord community for real-time support, tips, and discussions with other creators.
                  </p>
                  <a 
                    href="https://discord.gg/Amtkdgf9Jc" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#00C2FF]/20 hover:bg-[#00C2FF]/30 border border-[#00C2FF]/30 rounded-lg text-sm font-medium transition-colors"
                  >
                    Join Discord
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 bg-black/20 backdrop-blur-md py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} ThePromptSpace. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Docs;
